import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, LucideIcon, Users, Key, FileText, Settings as SettingsIcon, Gift, CreditCard, User as UserIcon } from 'lucide-react';
import { cn } from '@nimbitlabs/ui';
import { usePermissions, useIsFreeTier } from '@nimbitlabs/features';
import { useAuth } from '@nimbitlabs/auth';
import { useSidebarStore } from './stores/sidebarStore';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@nimbitlabs/ui';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@nimbitlabs/ui';

export interface NavItem {
  title: string;
  url: string;
  icon?: LucideIcon;
  badge?: string | number;
  isActive?: boolean;
  items?: NavItem[]; // Sub-items for nested navigation
  /**
   * Permission required to view this item
   */
  permission?: string;
  /**
   * Multiple permissions (any or all required)
   */
  permissions?: string[];
  /**
   * Require all permissions (default: false = any)
   */
  requireAllPermissions?: boolean;
  /**
   * Hide if user is on free tier
   */
  requirePaidPlan?: boolean;
  /**
   * Custom visibility check function
   */
  isVisible?: (context: { hasPermission: (p: string) => boolean; isFreeTier: boolean }) => boolean;
}

export interface NavGroup {
  title?: string;
  items: NavItem[];
}

interface AppSidebarProps {
  /**
   * App logo/branding component
   */
  logo?: React.ReactNode;
  
  /**
   * App-specific navigation items (domain-specific)
   */
  appNavItems: NavGroup[];
  
  /**
   * Whether to show default management items (Users, API Keys, Audit, Settings)
   * @default true
   */
  showManagementItems?: boolean;
  
  /**
   * Footer content (e.g., user menu, settings)
   */
  footer?: React.ReactNode;
  
  /**
   * Custom class name
   */
  className?: string;
  
  /**
   * Callback when nav item is clicked
   */
  onNavItemClick?: (item: NavItem) => void;
}

// Default management navigation items (admin only)
const defaultManagementItems: NavGroup = {
  title: 'Management',
  items: [
    { title: 'User Management', url: '/users', icon: Users, permission: 'users:read' },
    { title: 'API Keys', url: '/api-keys', icon: Key, permission: 'api_keys:read' },
    { title: 'Audit Log', url: '/audit', icon: FileText, permission: 'audit:read' },
    { title: 'Referrals', url: '/referrals', icon: Gift, permission: 'settings:read' },
    { title: 'Subscription', url: '/subscription', icon: CreditCard, permission: 'settings:read' },
  ],
};

// Default account navigation items (all users)
const defaultAccountItems: NavGroup = {
  title: 'Account',
  items: [
    { title: 'Profile', url: '/settings', icon: UserIcon, permission: 'settings:read' },
  ],
};

export function AppSidebar({
  logo,
  appNavItems,
  showManagementItems = true,
  footer,
  className,
  onNavItemClick,
}: AppSidebarProps) {
  const location = useLocation();
  const { state } = useSidebar();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  const isFreeTier = useIsFreeTier();
  const { user } = useAuth();
  
  // Get state from Zustand store
  const sidebarGroupsCollapsed = useSidebarStore((state) => state.sidebarGroupsCollapsed);
  
  // Check if user is admin (support both capitalized and lowercase)
  const isAdmin = user?.role === 'Admin' || user?.role === 'admin';
  
  // Combine app items with default items
  // - Admin users get both Management and Account groups
  // - Regular users only get Account group
  const allNavGroups = showManagementItems
    ? isAdmin
      ? [...appNavItems, defaultManagementItems, defaultAccountItems]
      : [...appNavItems, defaultAccountItems]
    : appNavItems;

  const isItemActive = (item: NavItem) => {
    if (item.isActive !== undefined) return item.isActive;
    return location.pathname === item.url || location.pathname.startsWith(item.url + '/');
  };

  const isItemVisible = (item: NavItem): boolean => {
    // Custom visibility check
    if (item.isVisible) {
      return item.isVisible({ hasPermission, isFreeTier });
    }

    // Always show items that require paid plan (will be locked with badge)
    // even if user lacks permission - this shows what they can unlock
    if (item.requirePaidPlan) {
      return true;
    }

    // For non-paid items, hide if user lacks permission
    if (item.permission && !hasPermission(item.permission)) {
      return false;
    }

    if (item.permissions) {
      const checkFn = item.requireAllPermissions ? hasAllPermissions : hasAnyPermission;
      if (!checkFn(item.permissions)) {
        return false;
      }
    }

    return true;
  };

  const isItemLocked = (item: NavItem): boolean => {
    // Item is locked if it requires paid plan and user is on free tier
    return item.requirePaidPlan === true && isFreeTier;
  };

  const renderNavItem = (item: NavItem) => {
    const active = isItemActive(item);
    const Icon = item.icon;

    // If item has sub-items, render as collapsible
    if (item.items && item.items.length > 0) {
      // When groups are collapsed, close all. Otherwise use active state.
      const shouldBeOpen = sidebarGroupsCollapsed ? false : active;
      
      return (
        <Collapsible 
          key={item.title}
          defaultOpen={shouldBeOpen}
          onOpenChange={() => {}}
          className="group/collapsible"
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton
                className={cn(
                  'h-10 px-3',
                  active && 'bg-accent', 
                  'transition-all duration-200',
                  state === "collapsed" && 'justify-center !p-2'
                )}
                tooltip={item.title}
              >
                {item.icon && <item.icon className="h-4 w-4 transition-transform duration-200" />}
                {state !== "collapsed" && <span className="transition-opacity duration-200">{item.title}</span>}
                {state !== "collapsed" && item.badge && (
                  <span className="ml-auto text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full transition-all duration-200">
                    {item.badge}
                  </span>
                )}
                {state !== "collapsed" && <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-300 ease-in-out group-data-[state=open]/collapsible:rotate-90" />}
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent className="transition-all duration-300 ease-in-out data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden">
              <SidebarMenuSub className="ml-3 border-l-2 border-sidebar-border/50 py-1">
                {item.items?.map((subItem, index) => (
                  <SidebarMenuSubItem 
                    key={subItem.title}
                    className="animate-in fade-in slide-in-from-left-2"
                    style={{ animationDelay: `${index * 50}ms`, animationDuration: '200ms' }}
                  >
                    <SidebarMenuSubButton
                      asChild
                      isActive={isItemActive(subItem)}
                      className="transition-all duration-200 hover:translate-x-1 h-9 px-3"
                    >
                      <Link
                        to={subItem.url}
                        onClick={() => onNavItemClick?.(subItem)}
                      >
                        {subItem.icon && <subItem.icon className="h-4 w-4 transition-transform duration-200" />}
                        <span className="transition-all duration-200">{subItem.title}</span>
                        {subItem.badge && (
                          <span className="ml-auto text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                            {subItem.badge}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    // Regular nav item
    const locked = isItemLocked(item);
    
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton
          asChild={!locked}
          isActive={active}
          tooltip={locked ? `${item.title} (Upgrade Required)` : item.title}
          className={cn(
            'h-10 px-3',
            state === "collapsed" && 'justify-center !p-2',
            locked && 'opacity-60 cursor-not-allowed'
          )}
        >
          {locked ? (
            <div className="flex items-center gap-2 w-full">
              {Icon && <Icon className="h-4 w-4" />}
              {state !== "collapsed" && <span>{item.title}</span>}
              {state !== "collapsed" && (
                <span className="ml-auto text-xs bg-amber-500/20 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-full font-medium">
                  +PRO
                </span>
              )}
            </div>
          ) : (
            <Link to={item.url} onClick={() => onNavItemClick?.(item)}>
              {Icon && <Icon className="h-4 w-4" />}
              {state !== "collapsed" && <span>{item.title}</span>}
              {state !== "collapsed" && item.badge && (
                <span className="ml-auto text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
            </Link>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className={className}>
      {logo && (
        <SidebarHeader className="px-4 py-4">
          <div className="flex items-center gap-2">
            {logo}
          </div>
        </SidebarHeader>
      )}

      <SidebarContent className="px-2">
        {allNavGroups.map((group, index) => {
          // Filter visible items
          const visibleItems = group.items.filter(isItemVisible);
          
          // Don't render empty groups
          if (visibleItems.length === 0) return null;
          
          // Determine if this group should be open
          const groupShouldBeOpen = sidebarGroupsCollapsed ? false : true;
          
          return (
            <Collapsible
              key={group.title || `group-${index}`}
              open={groupShouldBeOpen}
              className="group/collapsible mb-2"
            >
              <SidebarGroup className="py-2">
                <CollapsibleTrigger asChild>
                  <SidebarGroupLabel className="cursor-pointer bg-sidebar-accent hover:bg-sidebar-accent/80 rounded-md transition-colors px-3 py-3 mb-2 h-11 flex items-center">
                    <ChevronRight className="mr-2 h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    {group.title}
                  </SidebarGroupLabel>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu className="space-y-1">
                      {visibleItems.map(renderNavItem)}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          );
        })}
      </SidebarContent>

      {footer && (
        <SidebarFooter>
          {footer}
        </SidebarFooter>
      )}
    </Sidebar>
  );
}
